import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { Mail } from "lucide-react";
import { useAuthStore } from "../../stores/auth";
import { AuthLayout } from "../../components/layout/AuthLayout";
import { Input } from "../../components/ui/Input";
import { PasswordInput } from "../../components/ui/PasswordInput";
import { Button } from "../../components/ui/Button";

export function SignupPage() {
  const signup = useAuthStore((state) => state.signup);
  const error = useAuthStore((state) => state.error);
  const clearError = useAuthStore((state) => state.clearError);
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/app/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    try {
      await signup(email, password);
      navigate(from, { replace: true });
    } catch {
      // Error rendered from the store.
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthLayout>
      <h1>Create your account</h1>
      <p className="auth-card__subtitle">
        Start building your professional website in minutes — no credit card required.
      </p>

      <form className="auth-form" onSubmit={onSubmit}>
        <Input
          label="Email address"
          type="email"
          name="email"
          autoComplete="email"
          required
          placeholder="you@example.com"
          icon={<Mail size={16} />}
          value={email}
          onChange={(event) => {
            clearError();
            setEmail(event.target.value);
          }}
        />

        <PasswordInput
          label="Password"
          name="password"
          autoComplete="new-password"
          required
          minLength={8}
          maxLength={72}
          placeholder="Create a strong password"
          hint="Must be at least 8 characters"
          value={password}
          onChange={(event) => {
            clearError();
            setPassword(event.target.value);
          }}
        />

        {error ? (
          <div className="form-error" role="alert">
            {error}
          </div>
        ) : null}

        <Button type="submit" loading={pending} fullWidth size="lg">
          Create account
        </Button>
      </form>

      <div className="auth-card__footer">
        <p>
          Already have an account?{" "}
          <Link to="/login" state={location.state}>Sign in</Link>
        </p>
      </div>
    </AuthLayout>
  );
}
